import React, { useRef } from 'react';
import {
  // Autoplay is not used in NavCarousel, can be removed for clarity if not needed
  HashNavigation,
  Navigation,
  Pagination,
} from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation'; // Keep this for base navigation styles, even if overridden

type Props = {
  children: React.ReactNode[] | React.ReactNode;
  IndexHandler?: (newActiveIndex: number) => void;
};

export const Carousel = ({ children, IndexHandler }: Props) => {
  return (
    <>
      <Swiper
        autoplay={{
          delay: 3500,
          disableOnInteraction: false,
        }}
        centeredSlides={true}
        modules={[Pagination, Navigation]}
        navigation={false}
        pagination={{
          clickable: true,
        }}
        slidesPerView={1.3}
        spaceBetween={20}
        speed={900}
        onSlideChange={(swiper) => IndexHandler?.(swiper.activeIndex)}
      >
        {React.Children.map(children, (child) => {
          return <SwiperSlide>{child}</SwiperSlide>;
        })}
      </Swiper>
    </>
  );
};

export const NavCarousel = ({ children, IndexHandler }: Props) => {
  const prevRef = useRef<HTMLDivElement>(null);
  const nextRef = useRef<HTMLDivElement>(null);
  const paginationRef = useRef<HTMLDivElement>(null);

  return (
    <div className="relative w-full mx-auto">
      <div className="bg-white rounded-none md:rounded-2xl">
        <Swiper
          className="
            // This is still useful to ensure Swiper's default pseudo-element content is empty,
            // as it might still apply if not explicitly cleared, even if you control the parent.
            [&_.swiper-button-prev::after]:!content-['']
            [&_.swiper-button-next::after]:!content-['']
            
            // Keep disabled styles here as they apply to the *element* that Swiper flags as disabled.
            [&_.swiper-button-disabled]:!bg-gray-200
            [&_.swiper-button-disabled]:!text-gray-400
          "
          hashNavigation={{
            watchState: true,
          }}
          modules={[Pagination, Navigation, HashNavigation]}
          navigation={{}} // Empty object, as elements are set via refs
          pagination={{
            clickable: true,
            el: paginationRef.current,
            bulletClass:
              'swiper-pagination-bullet bg-gray-400 !w-2 !h-2 !mx-1 !opacity-100',
            bulletActiveClass:
              'swiper-pagination-bullet-active !bg-[#3366FF] !w-5 !rounded-lg !opacity-100',
          }}
          slidesPerView={1}
          spaceBetween={20}
          speed={900}
          onBeforeInit={(swiper) => {
            if (
              swiper.params.navigation &&
              typeof swiper.params.navigation !== 'boolean'
            ) {
              // Ensure the references are set before Swiper initializes navigation
              swiper.params.navigation.prevEl = prevRef.current;
              swiper.params.navigation.nextEl = nextRef.current;
            }
            if (
              swiper.params.pagination &&
              typeof swiper.params.pagination !== 'boolean'
            ) {
              swiper.params.pagination.el = paginationRef.current;
            }
          }}
          onSlideChange={(swiper) => IndexHandler?.(swiper.activeIndex)}
        >
          {/* Apply all styling directly to these custom button divs */}
          <div
            ref={prevRef}
            // Add all the desired styles here directly.
            // Swiper will add `swiper-button-prev` to this div.
            className="
              swiper-button-prev
              absolute left-2 z-[999] ml-6
              !w-10 !h-10 !rounded-lg
              !bg-[#3366FF]
              hover:!bg-blue-700
              flex items-center justify-center
              cursor-pointer
              !opacity-100
              !shadow-none
              !border-0
            "
          >
            <svg
              className="h-6 w-6 !text-white" // SVG styling
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 19l-7-7 7-7"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
          </div>
          <div
            ref={nextRef}
            // Apply all desired styles here directly.
            className="
              swiper-button-next
              absolute right-2 z-[999] mr-6
              !w-10 !h-10 !rounded-lg
              !bg-[#3366FF]
              hover:!bg-blue-700
              flex items-center justify-center
              cursor-pointer
              !opacity-100
              !shadow-none
              !border-0
            "
          >
            <svg
              className="h-6 w-6 !text-white" // SVG styling
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 5l7 7-7 7"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
          </div>
          {React.Children.map(children, (child) => (
            <SwiperSlide>{child}</SwiperSlide>
          ))}
        </Swiper>
      </div>
      <div
        ref={paginationRef}
        className="custom-pagination !-bottom-8 flex justify-center space-x-2 mt-5"
      />
    </div>
  );
};
